/**
 * Upkeep Budget Monitoring Utility Functions
 * 
 * Helper functions for Upkeep Budget Monitoring module
 */

import {
  UpkeepBudgetDataRaw,
  UpkeepMaterialDataRaw,
  UpkeepBudgetRow,
  UpkeepBudgetHierarchy,
  UpkeepBudgetGrouped
} from './types';

/**
 * Format number with 2 decimal places
 * Returns '-' for null, undefined, NaN, Infinity, or zero values
 */
export const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  if (!Number.isFinite(value)) return '-';
  if (value === 0) return '-';
  return value.toLocaleString('id-ID', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Calculate variance percentage
 * Variance = ((Actual - Budget) / Budget) * 100
 * If budget is 0 and actual is 0, variance is 0
 * If budget is 0 and actual > 0, variance is null (or 100% over)
 */
export const calculateVariance = (actual: number, budget: number): number | null => {
  if (budget === 0 && actual === 0) return 0;
  if (budget === 0) return null;
  return ((actual - budget) / budget) * 100;
};

/**
 * Format variance percentage for display
 * Returns '-' for null, Infinity, or zero variance
 */
export const formatVariance = (variance: number | null): string => {
  if (variance === null) return '-';
  if (!Number.isFinite(variance)) return '-';
  if (variance === 0) return '-';
  const sign = variance > 0 ? '+' : '';
  return `${sign}${variance.toFixed(2)}%`;
};

/**
 * Safe division helper
 */
const safeDivide = (numerator: number, denominator: number): number | null => {
  if (denominator === 0) return null;
  return numerator / denominator;
};

/**
 * Transform raw API data to display rows
 * Each activity generates 3 rows: UOM, HK, HK/UOM
 * 
 * Highlighting rules:
 * - Orange on UOM row's Actual columns: work_act > 0 AND work_bgt = 0 (actual without budget)
 * - Yellow on HK/UOM row's Actual columns: (hk_act/work_act) > (hk_bgt/work_bgt) (exceeds budget)
 */
export const transformToDisplayRows = (
  upkeepData: UpkeepBudgetDataRaw[],
  materialData: UpkeepMaterialDataRaw[] = []
): UpkeepBudgetRow[] => {
  const rows: UpkeepBudgetRow[] = [];

  const materialsByActivity = new Map<number, UpkeepMaterialDataRaw[]>();
  materialData.forEach((material) => {
    const existing = materialsByActivity.get(material.cost_activity_id) ?? [];
    existing.push(material);
    materialsByActivity.set(material.cost_activity_id, existing);
  });

  upkeepData.forEach((item, index) => {
    const { coa, coa_group, activity, uom, uom2, cost_activity_id } = item;

    // Row 1: UOM (dynamic parameter from uom field)
    const mtdActualUom = item.work_act_mtd;
    const mtdBudgetUom = item.work_bgt_mtd;
    const ytdActualUom = item.work_act_ytd;
    const ytdBudgetUom = item.work_bgt_ytd;

    // Orange highlight for UOM row: actual exists but no budget
    const highlightMtdActualOrange = mtdActualUom > 0 && mtdBudgetUom === 0;
    const highlightYtdActualOrange = ytdActualUom > 0 && ytdBudgetUom === 0;

    rows.push({
      block: item.block,
      coa,
      coaGroup: coa_group,
      activity,
      parameter: uom,
      rowType: 'uom',
      uom,
      uom2,
      costActivityId: cost_activity_id,
      mtdActual: mtdActualUom,
      mtdBudget: mtdBudgetUom,
      mtdVariance: calculateVariance(mtdActualUom, mtdBudgetUom),
      ytdActual: ytdActualUom,
      ytdBudget: ytdBudgetUom,
      ytdVariance: calculateVariance(ytdActualUom, ytdBudgetUom),
      highlightMtdActualOrange,
      highlightYtdActualOrange,
      highlightMtdActualYellow: false,
      highlightYtdActualYellow: false,
      isFirstRow: true,
      activityIndex: index,
    });

    if (uom2) {
      const mtdActualUom2 = item.work_act2_mtd;
      const ytdActualUom2 = item.work_act2_ytd;

      rows.push({
        block: item.block,
        coa,
        coaGroup: coa_group,
        activity,
        parameter: uom2,
        rowType: 'uom2',
        uom,
        uom2,
        costActivityId: cost_activity_id,
        mtdActual: mtdActualUom2,
        mtdBudget: null,
        mtdVariance: null,
        ytdActual: ytdActualUom2,
        ytdBudget: null,
        ytdVariance: null,
        highlightMtdActualOrange: mtdActualUom2 > 0,
        highlightYtdActualOrange: ytdActualUom2 > 0,
        highlightMtdActualYellow: false,
        highlightYtdActualYellow: false,
        isFirstRow: false,
        activityIndex: index,
      });

      // const mtdActualRatio2 = safeDivide(item.hk_act_mtd, item.work_act2_mtd);
      // const ytdActualRatio2 = safeDivide(item.hk_act_ytd, item.work_act2_ytd);

      // rows.push({
      //   coa,
      //   coaGroup: coa_group,
      //   activity,
      //   parameter: `HK/${uom2}`,
      //   rowType: 'hk_per_uom2',
      //   uom,
      //   uom2,
      //   costActivityId: cost_activity_id,
      //   mtdActual: mtdActualRatio2,
      //   mtdBudget: null,
      //   mtdVariance: null,
      //   ytdActual: ytdActualRatio2,
      //   ytdBudget: null,
      //   ytdVariance: null,
      //   highlightMtdActualOrange: false,
      //   highlightYtdActualOrange: false,
      //   highlightMtdActualYellow: false,
      //   highlightYtdActualYellow: false,
      //   isFirstRow: false,
      //   activityIndex: index,
      // });
    }

    // Row 2: HK (static)
    const mtdActualHk = item.hk_act_mtd;
    const mtdBudgetHk = item.hk_bgt_mtd;
    const ytdActualHk = item.hk_act_ytd;
    const ytdBudgetHk = item.hk_bgt_ytd;

    rows.push({
      block: item.block,
      coa,
      coaGroup: coa_group,
      activity,
      parameter: 'HK',
      rowType: 'hk',
      uom,
      uom2,
      costActivityId: cost_activity_id,
      mtdActual: mtdActualHk,
      mtdBudget: mtdBudgetHk,
      mtdVariance: calculateVariance(mtdActualHk, mtdBudgetHk),
      ytdActual: ytdActualHk,
      ytdBudget: ytdBudgetHk,
      ytdVariance: calculateVariance(ytdActualHk, ytdBudgetHk),
      highlightMtdActualOrange: false,
      highlightYtdActualOrange: false,
      highlightMtdActualYellow: false,
      highlightYtdActualYellow: false,
      isFirstRow: false,
      activityIndex: index,
    });

    // Row 3: HK/UOM (calculated)
    const mtdActualRatio = safeDivide(item.hk_act_mtd, item.work_act_mtd);
    const mtdBudgetRatio = safeDivide(item.hk_bgt_mtd, item.work_bgt_mtd);
    const ytdActualRatio = safeDivide(item.hk_act_ytd, item.work_act_ytd);
    const ytdBudgetRatio = safeDivide(item.hk_bgt_ytd, item.work_bgt_ytd);

    // Yellow highlight for HK/UOM row: actual exceeds budget
    const highlightMtdActualYellow =
      mtdActualRatio !== null &&
      mtdBudgetRatio !== null &&
      mtdBudgetRatio > 0 &&
      mtdActualRatio > mtdBudgetRatio;

    const highlightYtdActualYellow =
      ytdActualRatio !== null &&
      ytdBudgetRatio !== null &&
      ytdBudgetRatio > 0 &&
      ytdActualRatio > ytdBudgetRatio;

    rows.push({
      block: item.block,
      coa,
      coaGroup: coa_group,
      activity,
      parameter: `HK/${uom}`,
      rowType: 'hk_per_uom',
      uom,
      uom2,
      costActivityId: cost_activity_id,
      mtdActual: mtdActualRatio,
      mtdBudget: mtdBudgetRatio,
      mtdVariance: mtdActualRatio !== null && mtdBudgetRatio !== null
        ? calculateVariance(mtdActualRatio, mtdBudgetRatio)
        : null,
      ytdActual: ytdActualRatio,
      ytdBudget: ytdBudgetRatio,
      ytdVariance: ytdActualRatio !== null && ytdBudgetRatio !== null
        ? calculateVariance(ytdActualRatio, ytdBudgetRatio)
        : null,
      highlightMtdActualOrange: false,
      highlightYtdActualOrange: false,
      highlightMtdActualYellow,
      highlightYtdActualYellow,
      isFirstRow: false,
      activityIndex: index,
    });



    const materialRows = materialsByActivity.get(cost_activity_id) ?? [];
    materialRows.forEach((material) => {
      const mtdActualMaterial = material.qty_act_mtd;
      const mtdBudgetMaterial = material.qty_bgt_mtd;
      const ytdActualMaterial = material.qty_act_ytd;
      const ytdBudgetMaterial = material.qty_bgt_ytd;

      const highlightMtdMaterialOrange = mtdActualMaterial > 0 && mtdBudgetMaterial === 0;
      const highlightYtdMaterialOrange = ytdActualMaterial > 0 && ytdBudgetMaterial === 0;

      rows.push({
        block: item.block ?? material.block,
        coa,
        coaGroup: coa_group,
        activity,
        parameter: material.product_name,
        rowType: 'material',
        uom,
        uom2,
        costActivityId: cost_activity_id,
        materialProductId: material.product_id,
        mtdActual: mtdActualMaterial,
        mtdBudget: mtdBudgetMaterial,
        mtdVariance: calculateVariance(mtdActualMaterial, mtdBudgetMaterial),
        ytdActual: ytdActualMaterial,
        ytdBudget: ytdBudgetMaterial,
        ytdVariance: calculateVariance(ytdActualMaterial, ytdBudgetMaterial),
        highlightMtdActualOrange: highlightMtdMaterialOrange,
        highlightYtdActualOrange: highlightYtdMaterialOrange,
        highlightMtdActualYellow: false,
        highlightYtdActualYellow: false,
        isFirstRow: false,
        activityIndex: index,
      });
    });
  });

  const isValEmpty = (val: number | null | undefined) => val === 0 || val === null || val === undefined || Number.isNaN(val);
  return rows.filter(
    row => !(isValEmpty(row.mtdActual) && isValEmpty(row.mtdBudget) && isValEmpty(row.ytdActual) && isValEmpty(row.ytdBudget))
  );
};

/**
 * Transform rows into 3-level hierarchy: COA Group → COA → Activity
 */
export const transformToHierarchy = (rows: UpkeepBudgetRow[]): UpkeepBudgetHierarchy[] => {
  const hierarchyMap: Map<string, UpkeepBudgetHierarchy> = new Map();

  rows.forEach((row) => {
    const { coaGroup, coa, activity } = row;

    // Level 1: COA Group
    if (!hierarchyMap.has(coaGroup)) {
      hierarchyMap.set(coaGroup, {
        coaGroup,
        coaList: [],
      });
    }

    const coaGroupData = hierarchyMap.get(coaGroup)!;

    // Level 2: COA
    let coaData = coaGroupData.coaList.find(c => c.coa === coa);
    if (!coaData) {
      coaData = { coa, activities: [] };
      coaGroupData.coaList.push(coaData);
    }

    // Level 3: Activity
    let activityData = coaData.activities.find(a => a.activity === activity);
    if (!activityData) {
      activityData = { activity, rows: [] };
      coaData.activities.push(activityData);
    }

    activityData.rows.push(row);
  });

  return Array.from(hierarchyMap.values());
};

/**
 * Group rows by COA for display (legacy support)
 */
export const groupRowsByCoa = (rows: UpkeepBudgetRow[]): UpkeepBudgetGrouped[] => {
  const grouped: Map<string, UpkeepBudgetGrouped> = new Map();

  rows.forEach((row) => {
    if (!grouped.has(row.coa)) {
      grouped.set(row.coa, {
        coa: row.coa,
        coaGroup: row.coaGroup,
        activities: [],
      });
    }

    const coaGroupData = grouped.get(row.coa)!;
    let activityGroup = coaGroupData.activities.find(
      (a: { activity: string; rows: UpkeepBudgetRow[] }) => a.activity === row.activity
    );

    if (!activityGroup) {
      activityGroup = { activity: row.activity, rows: [] };
      coaGroupData.activities.push(activityGroup);
    }

    activityGroup.rows.push(row);
  });

  return Array.from(grouped.values());
};

/**
 * Get month name in Indonesian
 */
export const getMonthName = (monthNumber: string): string => {
  const months = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
  ];
  const index = Number.parseInt(monthNumber, 10) - 1;
  return months[index] || '';
};

/**
 * Generate month options for filter
 */
export const getMonthOptions = (): { value: string; label: string }[] => {
  const months = [
    { value: '01', label: 'Januari' },
    { value: '02', label: 'Februari' },
    { value: '03', label: 'Maret' },
    { value: '04', label: 'April' },
    { value: '05', label: 'Mei' },
    { value: '06', label: 'Juni' },
    { value: '07', label: 'Juli' },
    { value: '08', label: 'Agustus' },
    { value: '09', label: 'September' },
    { value: '10', label: 'Oktober' },
    { value: '11', label: 'November' },
    { value: '12', label: 'Desember' },
  ];
  return months;
};

/**
 * Generate year options for filter (last 5 years + current + next)
 */
export const getYearOptions = (): { value: string; label: string }[] => {
  const currentYear = new Date().getFullYear();
  const years: { value: string; label: string }[] = [];

  for (let year = currentYear + 1; year >= currentYear - 5; year--) {
    years.push({ value: year.toString(), label: year.toString() });
  }

  return years;
};
